import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import { TRPCClientError } from "@trpc/client";
import posthog from "posthog-js";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { trackSignUp } from "@/lib/dataLayer";
import { getPostHogAttributionProperties } from "@/lib/attribution";

// localStorage key prefix used to dedupe sign_up events across remounts and
// page reloads. Some auth flows (paywall-driven OAuth popups, mobile deep
// links) never hit the `?newSignup=true` redirect in App.tsx, so we detect
// "this user just signed up" here by comparing user.createdAt to now.
const SIGN_UP_TRACKED_KEY = (userId: string) => `solmi_sign_up_tracked_${userId}`;
// Window for treating createdAt as "just signed up" — generous to absorb
// clock skew between server and client.
const RECENT_SIGNUP_WINDOW_MS = 5 * 60 * 1000;

type UseAuthOptions = {
  redirectOnUnauthenticated?: boolean;
  redirectPath?: string;
};

export function useAuth(options?: UseAuthOptions) {
  const { redirectOnUnauthenticated = false, redirectPath = getLoginUrl() } =
    options ?? {};
  const utils = trpc.useUtils();
  const identifiedUserIdRef = useRef<string | null>(null);

  const meQuery = trpc.auth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60000, // Consider user data fresh for 60 seconds
    refetchInterval: false, // Never auto-refetch auth status
  });

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      utils.auth.me.setData(undefined, null);
      // Detach the email/userId from the device id so the next signed-out
      // session is captured anonymously instead of attributed to the
      // logged-out user.
      try {
        posthog.reset();
      } catch {}
      identifiedUserIdRef.current = null;
    },
  });

  const logout = useCallback(async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error: unknown) {
      if (
        error instanceof TRPCClientError &&
        error.data?.code === "UNAUTHORIZED"
      ) {
        return;
      }
      throw error;
    } finally {
      utils.auth.me.setData(undefined, null);
      await utils.auth.me.invalidate();
    }
  }, [logoutMutation, utils]);

  const state = useMemo(() => {
    localStorage.setItem(
      "manus-runtime-user-info",
      JSON.stringify(meQuery.data)
    );
    return {
      user: meQuery.data ?? null,
      loading: meQuery.isLoading || logoutMutation.isPending,
      error: meQuery.error ?? logoutMutation.error ?? null,
      isAuthenticated: Boolean(meQuery.data),
    };
  }, [
    meQuery.data,
    meQuery.error,
    meQuery.isLoading,
    logoutMutation.error,
    logoutMutation.isPending,
  ]);

  // Attach the user identity to the PostHog session so events, session
  // recordings, and error tracking become searchable by email. PostHog is
  // configured with `person_profiles: 'identified_only'` (see main.tsx),
  // so without this call user.email never reaches PostHog.
  //
  // This hook is ALSO the safety net for sign_up tracking. App.tsx fires
  // sign_up when the URL has `?newSignup=true`, but paywall-driven OAuth
  // popups and mobile deep-link auth flows skip that URL entirely. Catching
  // recently-created users here ensures every signup fires the event.
  useEffect(() => {
    const user = meQuery.data;
    if (!user?.id) return;
    if (identifiedUserIdRef.current === user.id) return;
    try {
      const attributionProperties = getPostHogAttributionProperties();
      posthog.identify(user.id, {
        email: user.email ?? undefined,
        name: user.name ?? undefined,
        ...attributionProperties,
      });
      identifiedUserIdRef.current = user.id;
    } catch {}

    // Fire sign_up event for brand new users that App.tsx's URL-based
    // detection missed. Dedupe via localStorage so we never double-fire.
    try {
      if (user.createdAt) {
        const createdAtMs = new Date(user.createdAt).getTime();
        const ageMs = Date.now() - createdAtMs;
        if (ageMs >= 0 && ageMs < RECENT_SIGNUP_WINDOW_MS) {
          const dedupeKey = SIGN_UP_TRACKED_KEY(user.id);
          if (!localStorage.getItem(dedupeKey)) {
            localStorage.setItem(dedupeKey, String(Date.now()));
            const method = user.loginMethod === "google" ? "google_oauth" : (user.loginMethod || "unknown");
            posthog.capture("sign_up", {
              method,
              ...getPostHogAttributionProperties(),
            });
            trackSignUp(method);
          }
        }
      }
    } catch {}
  }, [meQuery.data]);

  useEffect(() => {
    if (!redirectOnUnauthenticated) return;
    if (meQuery.isLoading || logoutMutation.isPending) return;
    if (state.user) return;
    if (typeof window === "undefined") return;
    if (window.location.pathname === redirectPath) return;

    window.location.href = redirectPath
  }, [
    redirectOnUnauthenticated,
    redirectPath,
    logoutMutation.isPending,
    meQuery.isLoading,
    state.user,
  ]);

  return {
    ...state,
    refresh: () => meQuery.refetch(),
    logout,
  };
}
